import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { State } from 'redux/initialState';
import { getUsers } from 'modules/profile/redux/userActions';
import { getPartner } from 'modules/partners/redux/partnerActions';
import { validateHasTenderOffer } from 'modules/_shared/api/validationActions';
import { getSocietyValue } from 'utils/filters';
import { getPartnerIncorporationDate } from 'modules/partners/utils/getPartnerIncorporationDate';
import { format } from 'date-fns';
import { dateFormat } from 'constants/formats';
import { Partner, User } from 'types';
import { store } from 'redux/store';

type CustomPartner = Partner & {
  societyValue: number;
  mainAdmin?: User | null;
};

export const usePartnerData = (partnerId: string, actualSociety: any) => {
  const dispatch = useDispatch();
  const user = useSelector((state: State) => state.user);
  const partners = useSelector((state: State) => state.partners);
  const tenderOffers = useSelector((state: State) => state.tenderOffers);

  const [partner, setPartner] = useState<CustomPartner | null>(null);
  const [incorporationDate, setIncorporationDate] = useState<string | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const fetchAdministrator = useCallback(
    async (adminId: string): Promise<User | null> => {
      try {
        const [admin] = (await dispatch(
          getUsers({ usersList: [adminId] })
        )) as unknown as User[];
        return admin;
      } catch (error) {
        console.error('Failed to fetch administrator:', error);
        return null;
      }
    },
    [dispatch]
  );

  const fetchPartnerData = useCallback(async (): Promise<Partner | null> => {
    if (!partnerId) return null;

    try {
      const currentPartner = await store.dispatch(getPartner(partnerId));
      return currentPartner;
    } catch (error) {
      console.error('Failed to fetch partner data:', error);
      throw error;
    }
  }, [partnerId]);

  useEffect(() => {
    if (!user || !actualSociety || !partnerId) return;

    const processPartnerData = async () => {
      try {
        setIsLoading(true);
        setIsError(false);

        const [currentPartner, administrator] = await Promise.all([
          fetchPartnerData(),
          fetchAdministrator(actualSociety.administrators[0]),
        ]);

        if (!currentPartner) {
          throw new Error('Failed to fetch partner data');
        }

        const societyValue = getSocietyValue(actualSociety);
        const registrationDate = await getPartnerIncorporationDate(
          actualSociety,
          currentPartner
        );

        const formattedDate = registrationDate
          ? format(new Date(registrationDate), dateFormat)
          : null;

        const updatedPartner: CustomPartner = {
          ...currentPartner,
          societyValue,
          mainAdmin: administrator,
        };

        setPartner(updatedPartner);
        setIncorporationDate(formattedDate);
      } catch (error) {
        console.error('Error processing partner data:', error);
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };

    processPartnerData();
  }, [
    user,
    actualSociety,
    partnerId,
    partners,
    fetchPartnerData,
    fetchAdministrator,
  ]);

  // Handle tender offer validation
  useEffect(() => {
    if (actualSociety && tenderOffers.length && partnerId) {
      dispatch(validateHasTenderOffer(tenderOffers, partnerId));
    }
  }, [actualSociety, tenderOffers, partnerId, dispatch]);

  return {
    partner,
    incorporationDate,
    fetchPartnerData,
    isLoading,
    isError,
  };
};
